import { NetworkError, errorCodes, toApiError, ApiError } from "./errors";
import { toSearchParams, type ListQuery, type PagedResult } from "./listQuery";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  /** Query string parameters; `null`/`undefined` are skipped. A `URLSearchParams` is used as is. */
  query?: Record<string, QueryValue> | URLSearchParams;
  /** JSON body. */
  body?: unknown;
  /** Raw body (e.g. an uploaded `.xlsx`), sent with `contentType`; mutually exclusive with `body`. */
  content?: Blob | ArrayBuffer;
  /** Media type of `content` (default `application/octet-stream`). */
  contentType?: string;
  /** `blob` reads a successful response as binary (file downloads); errors are still problem details. */
  responseType?: "json" | "blob";
  /** Row version for optimistic concurrency (sent as `If-Match`, ADR-0010/D-7). */
  ifMatch?: string | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** A successful response with its row version (ETag), if the server sent one. */
export interface ApiResponse<T> {
  data: T;
  status: number;
  /** ETag header (row version). Send it back as `ifMatch` on update. */
  version: string | null;
  headers: Headers;
}

/** A downloaded file. */
export interface DownloadedFile {
  blob: Blob;
  /** File name from `Content-Disposition`, if the server sent one. */
  fileName: string | null;
}

/** A resource together with the row version it was read at. */
export interface Versioned<T> {
  data: T;
  version: string | null;
}

export interface ApiClientOptions {
  /** Base URL, e.g. `/api` (same origin through the dev proxy / reverse proxy) or `https://t1.example.com/api`. */
  baseUrl: string;
  /** Current UI language, sent as `Accept-Language` so server-side text (PDFs, e-mails) matches (ADR-0009). */
  getLanguage?: () => string | null | undefined;
  /** Bearer token hook (ADR-0003/D-2: tokens live in memory, obtained from the OIDC client). */
  getAccessToken?: () => string | null | undefined | Promise<string | null | undefined>;
  /**
   * Called once per 401 answer, e.g. to renew the token silently or to start a sign-in redirect. Resolving `true`
   * (a new token is available) retries the request once with the new token; anything else rejects with the 401.
   */
  onUnauthorized?: (error: ApiError) => boolean | Promise<boolean> | undefined;
  /** Extra headers on every request (e.g. the development tenant header or the support-session header). */
  getHeaders?: () => Record<string, string> | undefined;
  /** API version header (Asp.Versioning). */
  apiVersion?: string;
  /** Fetch implementation; defaults to the global `fetch` (tests and dev mocks inject their own). */
  fetch?: typeof fetch;
}

export interface ApiClient {
  request<T>(method: HttpMethod, path: string, options?: RequestOptions): Promise<ApiResponse<T>>;
  get<T>(path: string, options?: Omit<RequestOptions, "body" | "ifMatch">): Promise<T>;
  /** GET returning the data with its row version, for edit screens. */
  getVersioned<T>(path: string, options?: Omit<RequestOptions, "body" | "ifMatch">): Promise<Versioned<T>>;
  /** GET a list endpoint with the shared paging/sorting/filtering model. */
  list<T>(
    path: string,
    query: ListQuery,
    options?: Omit<RequestOptions, "body" | "ifMatch" | "query">,
  ): Promise<PagedResult<T>>;
  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "body">): Promise<ApiResponse<T>>;
  put<T>(path: string, body: unknown, options?: Omit<RequestOptions, "body">): Promise<ApiResponse<T>>;
  patch<T>(path: string, body: unknown, options?: Omit<RequestOptions, "body">): Promise<ApiResponse<T>>;
  delete<T = void>(path: string, options?: Omit<RequestOptions, "body">): Promise<ApiResponse<T>>;
  /** POST a raw file body (import framework: the `.xlsx` is the request body, no multipart). */
  upload<T>(
    path: string,
    content: Blob | ArrayBuffer,
    contentType: string,
    options?: Omit<RequestOptions, "body" | "content" | "contentType">,
  ): Promise<ApiResponse<T>>;
  /** GET a file (export, template) as a blob with its file name. */
  download(path: string, options?: Omit<RequestOptions, "body" | "content" | "ifMatch">): Promise<DownloadedFile>;
}

/** File name of a `Content-Disposition` header (`filename*=UTF-8''…` preferred over `filename=`). */
export function fileNameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/iu.exec(disposition);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/gu, ""));
    } catch {
      return null;
    }
  }
  const plain = /filename=("?)([^";]+)\1/iu.exec(disposition);
  return plain?.[2]?.trim() ?? null;
}

function joinUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//u.test(path)) return path;
  return `${baseUrl.replace(/\/+$/u, "")}/${path.replace(/^\/+/u, "")}`;
}

function buildQuery(query: RequestOptions["query"]): string {
  if (!query) return "";
  const params =
    query instanceof URLSearchParams
      ? query
      : new URLSearchParams(
          Object.entries(query).flatMap(([key, value]) =>
            value === null || value === undefined ? [] : [[key, String(value)]],
          ),
        );
  const text = params.toString();
  return text ? `?${text}` : "";
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) return undefined;
  const text = await response.text();
  if (text === "") return undefined;
  const type = response.headers.get("Content-Type") ?? "";
  if (type.includes("json")) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return undefined;
    }
  }
  return text;
}

/** Creates the typed fetch client used by every module (ADR-0010; generated endpoint wrappers build on it). */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const fetchImpl = options.fetch ?? ((input, init) => globalThis.fetch(input, init));

  async function request<T>(
    method: HttpMethod,
    path: string,
    init: RequestOptions = {},
    isRetry = false,
  ): Promise<ApiResponse<T>> {
    const headers = new Headers({ Accept: "application/json, application/problem+json" });
    const language = options.getLanguage?.();
    if (language) headers.set("Accept-Language", language);
    if (options.apiVersion) headers.set("api-version", options.apiVersion);
    const token = await options.getAccessToken?.();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    if (init.ifMatch) headers.set("If-Match", init.ifMatch);
    for (const [name, value] of Object.entries(options.getHeaders?.() ?? {})) headers.set(name, value);
    let body: string | Blob | ArrayBuffer | undefined;
    if (init.content !== undefined) {
      headers.set("Content-Type", init.contentType ?? "application/octet-stream");
      body = init.content;
    } else if (init.body !== undefined) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(init.body);
    }
    if (init.responseType === "blob") headers.set("Accept", "*/*");
    for (const [name, value] of Object.entries(init.headers ?? {})) headers.set(name, value);

    let response: Response;
    try {
      response = await fetchImpl(joinUrl(options.baseUrl, path) + buildQuery(init.query), {
        method,
        headers,
        ...(body === undefined ? {} : { body }),
        ...(init.signal ? { signal: init.signal } : {}),
      });
    } catch (cause) {
      // An abort is not an error the user must see; TanStack Query handles cancellation itself.
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new NetworkError(cause);
    }

    if (response.ok && init.responseType === "blob") {
      return {
        data: (await response.blob()) as T,
        status: response.status,
        version: response.headers.get("ETag"),
        headers: response.headers,
      };
    }
    const payload = await readBody(response);
    if (!response.ok) {
      const error = toApiError(response.status, payload, response.headers.get("traceparent")?.split("-")[1]);
      if (response.status === 401 && options.onUnauthorized) {
        const retry = await options.onUnauthorized(error);
        if (retry === true && !isRetry && !init.signal?.aborted) return request<T>(method, path, init, true);
      }
      throw error;
    }
    if (payload !== undefined && typeof payload === "string" && method === "GET") {
      throw new ApiError({ status: response.status, code: errorCodes.invalidResponse });
    }
    return {
      data: payload as T,
      status: response.status,
      version: response.headers.get("ETag"),
      headers: response.headers,
    };
  }

  return {
    request,
    async get<T>(path: string, init?: Omit<RequestOptions, "body" | "ifMatch">) {
      return (await request<T>("GET", path, init)).data;
    },
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- implements the generic interface
    async getVersioned<T>(path: string, init?: Omit<RequestOptions, "body" | "ifMatch">) {
      const response = await request<T>("GET", path, init);
      return { data: response.data, version: response.version };
    },
    async list<T>(path: string, query: ListQuery, init?: Omit<RequestOptions, "body" | "ifMatch" | "query">) {
      return (await request<PagedResult<T>>("GET", path, { ...init, query: toSearchParams(query) })).data;
    },
    post: (path, body, init) => request("POST", path, { ...init, body }),
    put: (path, body, init) => request("PUT", path, { ...init, body }),
    patch: (path, body, init) => request("PATCH", path, { ...init, body }),
    delete: (path, init) => request("DELETE", path, init),
    upload: (path, content, contentType, init) => request("POST", path, { ...init, content, contentType }),
    async download(path, init) {
      const response = await request<Blob>("GET", path, { ...init, responseType: "blob" });
      return { blob: response.data, fileName: fileNameOf(response.headers.get("Content-Disposition")) };
    },
  };
}
